import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Drawer, Modal, Spin, Typography } from 'antd';
import { Reorder } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import CustomInput from '../../../commonComponents/input';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useCategoriesQuery } from './hooks/useCategoriesQuery';
import { useDeleteCategory, useReorderCategories } from './hooks/useCategoryMutations';
import CategoryFormModal from './CategoryFormModal';
import { Category, CategoriesProps } from './types';
import { REORDER_DEBOUNCE_MS, SEARCH_DEBOUNCE_MS } from './constants';
import {
  ActionIconButton,
  ActionsCell,
  AddButton,
  AddItemsButton,
  CategoryNameText,
  CategoryRow,
  DrawerContentWrapper,
  EmptyState,
  ListWrapper,
  NoImagePlaceholder,
  PageWrapper,
  RowBody,
  RowThumbnailImg,
  RowThumbnailWrap,
  SearchWrapper,
  StatusTag,
} from './styles';

const { Text } = Typography;

const Categories: React.FC<CategoriesProps> = ({ onCategoryChanged }) => {
  const { isDarkMode } = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryOrder, setCategoryOrder] = useState<Array<number | string>>([]);
  const reorderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | string | null>(null);

  const { data: categories = [], isLoading, error, refetch } = useCategoriesQuery(debouncedSearch);
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteCategory();
  const { mutate: reorderMutate } = useReorderCategories();

  const categoryMap = useMemo(() => {
    const map = new Map<number | string, Category>();
    categories.forEach((category) => map.set(category.id, category));
    return map;
  }, [categories]);

  // Keep the locally-draggable order in sync with fresh server data (initial
  // load, refetch after create/delete, search, store switch, etc). Search
  // filtering now happens server-side, so `categories` is already the
  // matching set — no separate client-side text filter needed.
  useEffect(() => {
    setCategoryOrder(categories.map((category) => category.id));
  }, [categories]);

  useEffect(() => {
    return () => {
      if (reorderTimeoutRef.current) clearTimeout(reorderTimeoutRef.current);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(categoryMap.get(category.id) || category);
    setIsDrawerOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTargetId) return;
    deleteMutate(deleteTargetId, {
      onSuccess: () => {
        setDeleteTargetId(null);
        onCategoryChanged?.();
      },
      onError: () => setDeleteTargetId(null),
    });
  };

  // Same debounce-after-drag behavior as before: update the UI immediately,
  // but only persist the new order to the API 500ms after the last drag.
  const handleReorder = (newOrder: Array<number | string>) => {
    setCategoryOrder(newOrder);

    if (reorderTimeoutRef.current) clearTimeout(reorderTimeoutRef.current);
    reorderTimeoutRef.current = setTimeout(() => {
      const reorderedData = newOrder.map((id, index) => ({
        id: parseInt(String(id), 10),
        sequence_number: index + 1,
      }));
      reorderMutate(reorderedData);
    }, REORDER_DEBOUNCE_MS);
  };

  const drawerStyles = {
    body: { padding: 0, background: isDarkMode ? '#1a202c' : '#ffffff' },
    header: {
      background: isDarkMode ? '#1a202c' : '#ffffff',
      borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
      color: isDarkMode ? '#e0e0e0' : 'inherit',
    },
  };

  if (error) {
    return (
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <PageWrapper>
          <EmptyState $isDark={isDarkMode}>
            <Text type="danger">Unable to load categories. Please try again.</Text>
            <div style={{ marginTop: 12 }}>
              <AddButton onClick={() => refetch()}>Retry</AddButton>
            </div>
          </EmptyState>
        </PageWrapper>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <PageWrapper>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <AddButton type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreate} size="large">
            Add New Category
          </AddButton>
        </div>

        <SearchWrapper>
          <CustomInput
            placeholder="Search categories by name..."
            value={searchQuery}
            onChange={handleSearch}
            allowClear
            width="300px"
          />
        </SearchWrapper>

        {isLoading ? (
          <EmptyState $isDark={isDarkMode}>
            <Spin />
          </EmptyState>
        ) : categories.length === 0 && debouncedSearch.trim() ? (
          <EmptyState $isDark={isDarkMode}>
            <Text>No categories match your search.</Text>
          </EmptyState>
        ) : categories.length === 0 ? (
          <EmptyState $isDark={isDarkMode}>
            <Text>No categories found. Create your first category to get started.</Text>
            <div style={{ marginTop: 12 }}>
              <AddButton icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreate}>
                Create Your First Category
              </AddButton>
            </div>
          </EmptyState>
        ) : (
          <ListWrapper>
            <Reorder.Group
              axis="y"
              values={categoryOrder}
              onReorder={handleReorder}
              style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              {categoryOrder.map((id) => {
                const category = categoryMap.get(id);
                if (!category) return null;
                const itemCount = category.items?.length || 0;

                return (
                  <Reorder.Item key={id} value={id} style={{ listStyle: 'none' }}>
                    <CategoryRow $isDark={isDarkMode}>
                      <RowThumbnailWrap>
                        {category.picture ? (
                          <RowThumbnailImg src={category.picture} alt={category.category} />
                        ) : (
                          <NoImagePlaceholder>
                            <FontAwesomeIcon icon={faImage} fontSize={18} />
                          </NoImagePlaceholder>
                        )}
                      </RowThumbnailWrap>

                      <RowBody>
                        <CategoryNameText $isDark={isDarkMode}>{category.category}</CategoryNameText>
                        <StatusTag $isDark={isDarkMode}>{itemCount} {itemCount === 1 ? 'Item' : 'Items'}</StatusTag>
                        <AddItemsButton onClick={(e) => { e.stopPropagation(); handleEdit(category); }}>
                          <FontAwesomeIcon icon={faPlus} fontSize={11} />
                          Add Items
                        </AddItemsButton>
                      </RowBody>

                      <ActionsCell>
                        <Tooltip title="Edit">
                          <ActionIconButton
                            iconColor="#2D6CCA"
                            onClick={(e) => { e.stopPropagation(); handleEdit(category); }}
                          >
                            <FontAwesomeIcon icon={faPen} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                          </ActionIconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <ActionIconButton
                            iconColor="#f5222d"
                            onClick={(e) => { e.stopPropagation(); setDeleteTargetId(category.id); }}
                          >
                            <FontAwesomeIcon icon={faTrash} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                          </ActionIconButton>
                        </Tooltip>
                      </ActionsCell>
                    </CategoryRow>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </ListWrapper>
        )}

        <Drawer
          title={editingCategory ? 'Edit Category' : 'Add New Category'}
          placement="right"
          onClose={() => setIsDrawerOpen(false)}
          maskClosable={false}
          open={isDrawerOpen}
          width={760}
          destroyOnClose
          styles={drawerStyles}
        >
          <DrawerContentWrapper>
            <CategoryFormModal
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              editingCategory={editingCategory}
              onSuccess={() => {
                setIsDrawerOpen(false);
                onCategoryChanged?.();
              }}
            />
          </DrawerContentWrapper>
        </Drawer>

        <Modal
          open={!!deleteTargetId}
          onCancel={() => setDeleteTargetId(null)}
          onOk={confirmDelete}
          confirmLoading={isDeleting}
          title="Delete Category"
          okText="Yes, Delete"
          okButtonProps={{ danger: true }}
          centered
        >
          <Text>Are you sure you want to delete this category? This action cannot be undone.</Text>
        </Modal>
      </PageWrapper>
    </ConfigProvider>
  );
};

export default Categories;
